/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cva } from 'class-variance-authority';
import { GripHorizontal, Sparkles } from 'lucide-react';
import { FC } from 'react';

import { LiveAgentStepHeader } from './LiveAgentStepHeader';
import { Step } from './LiveAgentTypes';

import { cn } from '@/lib/utils';

export const LiveAgentStepSeparator: FC<{
    step: Step;
    index: number;
    parentIndex?: number;
    isFirst: boolean;
    queued?: boolean;
    inert?: boolean;
}> = ({ step, index, parentIndex, isFirst, queued, inert }) => {
    const { setNodeRef, attributes, listeners, isDragging, transform, transition } = useSortable({
        id: step.id,
        data: {
            index,
            parentIndex,
        },
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const variants = cva('', {
        variants: {
            dragging: {
                over: 'opacity-40 relative z-10',
            },
        },
    });

    return (
        <div
            key={step.id}
            style={inert ? {} : style}
            ref={setNodeRef}
            className={cn('rounded relative', variants({ dragging: isDragging && !inert ? 'over' : undefined }))}
        >
            <div className={cn('rounded transition-all duration-200', queued && 'bg-purple-100')}>
                {isFirst && (
                    <div className="flex items-center gap-2 p-1 whitespace-nowrap">
                        <LiveAgentStepHeader
                            icon={<Sparkles />}
                            title="Prompt"
                            subtitle={step.intentPrompt}
                            step={step}
                            variables={{}}
                            precomputedVariables={{}}
                        />
                    </div>
                )}
                <div className="flex items-center gap-2 whitespace-nowrap text-slate-400 px-1">
                    <div className="h-[1px] w-full rounded bg-current" />
                    <button {...attributes} {...listeners} className="mx-auto">
                        <span className="sr-only">Move step</span>
                        <GripHorizontal className="cursor-move" size="14" />
                    </button>
                    <div className="h-[1px] w-full rounded bg-current" />
                </div>
            </div>
        </div>
    );
};
